import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

interface Notice {
  id: string;
  productName: string;
  byNickname?: string;
}

interface Props {
  notices: Notice[];
  onDismiss: (id: string) => void;
}

const AUTO_DISMISS_MS = 5_000;

export const NoticeToast = ({ notices, onDismiss }: Props): JSX.Element => {
  const { t } = useTranslation();

  useEffect(() => {
    const timers = notices.map((n) =>
      window.setTimeout(() => onDismiss(n.id), AUTO_DISMISS_MS)
    );
    return () => timers.forEach((t) => clearTimeout(t));
  }, [notices, onDismiss]);

  const visible = notices.slice(-3);
  if (!visible.length) return <></>;
  if (typeof document === "undefined") return <></>;

  return createPortal(
    <div className="fixed bottom-32 inset-x-4 z-[80] space-y-2 max-w-md mx-auto pointer-events-none">
      {visible.map((n) => (
        <div
          key={n.id}
          className="bg-warm text-ink rounded-card px-4 py-3 shadow-warm-lg animate-slide-up pointer-events-auto flex items-start gap-3"
          role="status"
        >
          <span aria-hidden="true" className="text-lg">
            ℹ
          </span>
          <p className="flex-1 min-w-0 font-medium text-sm leading-snug">
            {n.byNickname
              ? t("notice.removedByOther", {
                  name: n.productName,
                  nickname: n.byNickname,
                  defaultValue: `« ${n.productName} » retiré par ${n.byNickname}`,
                })
              : t("notice.removedByOtherAnon", {
                  name: n.productName,
                  defaultValue: `« ${n.productName} » retiré par un autre participant`,
                })}
          </p>
          <button
            type="button"
            onClick={() => onDismiss(n.id)}
            className="text-ink/60 text-xs"
            aria-label={t("common.close", "Fermer")}
          >
            ✕
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
};
